import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDebounce } from '../Hooks/useDebounce';
import { useAutocompleteDropdownHeight } from '../Hooks/useAutocompleteDropdownHeight';
import { useAPI } from '../Hooks/useAPIs';

const SIGNAL_PAGE_SIZE = 100;

const toSignalOption = (option) => {
  if (typeof option === 'string') {
    return {
      signal: option,
      longname: '',
      network: '',
      message: '',
      selectionHint: '',
      resultType: 'signal',
      isAmbiguous: false,
      networkOptions: [],
    };
  }

  if (!option || typeof option !== 'object') {
    return null;
  }

  const signal = option.signal || option.name || option.value || '';
  if (!signal) {
    return null;
  }

  return {
    signal,
    longname: option.longname || option.description || '',
    network: option.network || '',
    message: option.message || '',
    selectionHint: option.selection_hint || '',
    resultType: option.result_type || (option.is_ambiguous ? 'group' : 'signal'),
    isAmbiguous: Boolean(option.is_ambiguous),
    networkOptions: Array.isArray(option.network_options) ? option.network_options : [],
  };
};

const normalizeSignalOptions = (rawOptions) => rawOptions.flatMap((option) => {
  if (option && typeof option === 'object' && option.result_type) {
    const normalizedOption = toSignalOption(option);
    return normalizedOption ? [normalizedOption] : [];
  }

  if (
    option &&
    typeof option === 'object' &&
    option.is_ambiguous &&
    Array.isArray(option.network_results) &&
    option.network_results.length > 0
  ) {
    const groupOption = toSignalOption({ ...option, result_type: 'group' });
    if (!groupOption) return [];
    const children = option.network_results
      .map((networkResult) => toSignalOption({
        ...option,
        ...networkResult,
        result_type: 'network_option',
        selection_hint: option.selection_hint,
      }))
      .filter(Boolean);
    return [groupOption, ...children];
  }

  const normalizedOption = toSignalOption(option);
  return normalizedOption ? [normalizedOption] : [];
});

const getSignalOptionId = (option) => `${option.signal}|${option.network || ''}`;
const getSignalOptionDisplayName = (option) => (
  option?.network ? `${option.signal} (${option.network})` : option?.signal || ''
);

const isGroupOption = (option) => option?.resultType === 'group';
const isNetworkOption = (option) => option?.resultType === 'network_option';

const SignalSearch = ({ filePath, isFileReady, signalCount, signals }) => {
  const { apiCall } = useAPI();
  const [signalSearchTerm, setSignalSearchTerm] = useState('');
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const debouncedSignalSearchTerm = useDebounce(signalSearchTerm, 300);
  const { containerRef, availableHeight, updateAvailableHeight } = useAutocompleteDropdownHeight();

  const handleAutocompleteOpen = useCallback(() => {
    setIsAutocompleteOpen(true);
    updateAvailableHeight();
  }, [updateAvailableHeight]);

  const { selectSignal, removeSignal, visualizedSignals, loadingSignals } = signals;

  const signalOptionsQuery = useInfiniteQuery({
    queryKey: ['visualize-signals-autocomplete', filePath, debouncedSignalSearchTerm],
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        file_path: filePath,
        limit: String(SIGNAL_PAGE_SIZE),
        offset: String(pageParam),
      });

      if (debouncedSignalSearchTerm?.trim()) {
        params.append('q', debouncedSignalSearchTerm.trim());
      }

      const response = await apiCall('GET', `/api/v1/signals/autocomplete/?${params.toString()}`);
      const data = response?.data;

      const rawOptions = (() => {
        if (Array.isArray(data?.display_results)) return data.display_results;
        if (Array.isArray(data?.signal)) return data.signal;
        if (Array.isArray(data?.results)) return data.results;
        if (Array.isArray(data)) return data;
        return [];
      })();

      const normalized = normalizeSignalOptions(rawOptions);

      const hasMore = typeof data?.has_more === 'boolean'
        ? data.has_more
        : typeof data?.hasMore === 'boolean'
          ? data.hasMore
          : normalized.length >= SIGNAL_PAGE_SIZE;

      return {
        results: normalized,
        hasMore,
        nextOffset: pageParam + SIGNAL_PAGE_SIZE,
      };
    },
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextOffset : undefined),
    initialPageParam: 0,
    enabled: isFileReady && !!filePath,
    keepPreviousData: true,
  });

  const signalOptions = useMemo(() => {
    const pages = signalOptionsQuery.data?.pages || [];
    const merged = pages.flatMap((page) => page.results || []);
    const seen = new Set();
    return merged.filter((item) => {
      if (!item?.signal) return false;
      const optionId = getSignalOptionId(item);
      if (seen.has(optionId)) return false;
      seen.add(optionId);
      return true;
    });
  }, [signalOptionsQuery.data]);

  const selectedSignalIds = useMemo(() => (
    new Set(
      visualizedSignals
        .map((signalItem) => signalItem.selectionId || '')
        .filter(Boolean)
    )
  ), [visualizedSignals]);

  const isSignalSelected = useCallback((signalOption) => {
    if (!signalOption?.signal) return false;
    const optionId = getSignalOptionId(signalOption);
    if (isGroupOption(signalOption)) {
      return selectedSignalIds.has(signalOption.signal);
    }
    return selectedSignalIds.has(optionId);
  }, [selectedSignalIds]);

  const handleSignalSelect = useCallback((signalOption) => {
    if (!signalOption?.signal) return;
    if (isSignalSelected(signalOption)) {
      const displayName = getSignalOptionDisplayName(signalOption);
      if (displayName) {
        removeSignal(displayName);
      }
      return;
    }
    selectSignal(signalOption);
  }, [removeSignal, selectSignal, isSignalSelected]);

  const handleListboxScroll = useCallback((event) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 40) {
      if (signalOptionsQuery.hasNextPage && !signalOptionsQuery.isFetchingNextPage) {
        signalOptionsQuery.fetchNextPage();
      }
    }
  }, [signalOptionsQuery]);

  if (!isFileReady) return null;

  return (
    <Box className="signal-search-container" ref={containerRef}>
      <Autocomplete
        open={isAutocompleteOpen}
        onOpen={handleAutocompleteOpen}
        onClose={() => setIsAutocompleteOpen(false)}
        fullWidth
        size="small"
        disableCloseOnSelect
        blurOnSelect={false}
        clearOnBlur={false}
        options={signalOptions}
        getOptionLabel={(option) => option.signal}
        isOptionEqualToValue={(option, value) => getSignalOptionId(option) === getSignalOptionId(value)}
        filterOptions={(x) => x}
        value={null}
        inputValue={signalSearchTerm}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'reset' || reason === 'selectOption') return;
          setSignalSearchTerm(newInputValue);
          setIsAutocompleteOpen(true);
        }}
        onChange={(event, newValue) => {
          if (newValue) {
            handleSignalSelect(newValue);
            setIsAutocompleteOpen(true);
          }
        }}
        loading={signalOptionsQuery.isFetching || loadingSignals.length > 0}
        ListboxProps={{
          onScroll: handleListboxScroll,
          style: { maxHeight: availableHeight },
          className: 'signal-search-listbox',
        }}
        slotProps={{
          paper: {
            className: 'signal-search-paper',
          },
          popper: {
            className: 'signal-search-popper',
            placement: 'bottom',
            modifiers: [
              { name: 'flip', enabled: false },
            ],
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className="signal-search-input"
            placeholder={`Search ${signalCount ? signalCount.toLocaleString() : ''} signals...`}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 1, mr: -0.5 }}>
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {(signalOptionsQuery.isFetching || loadingSignals.length > 0) ? (
                    <CircularProgress color="inherit" size={20} style={{ marginRight: 8 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const isSelected = isSignalSelected(option);
          const isGroup = isGroupOption(option);
          const isNetworkChild = isNetworkOption(option);
          
          return (
            <li
              {...props}
              key={getSignalOptionId(option)}
              className={`signal-search-option-li ${props.className || ''} ${isGroup ? 'signal-search-option-li-group' : ''} ${isNetworkChild ? 'signal-search-option-li-child' : ''}`}
            >
              <Box
                className={`signal-search-option-content ${isSelected ? 'selected' : ''} ${isGroup ? 'group' : ''} ${isNetworkChild ? 'child' : ''}`}
              >
                <Box className="signal-search-option-row">
                  <GraphicEqIcon className="signal-search-option-icon" />
                  <Typography
                    variant="body2"
                    className="signal-search-option-text"
                    sx={{ fontWeight: isGroup ? 600 : 400 }}
                  >
                    {option.signal}
                  </Typography>
                  <Box className="signal-search-option-badges">
                    {isGroup && (
                      <span className="signal-search-option-badge signal-search-option-badge-group">
                        All Networks
                      </span>
                    )}
                    {!isGroup && option.network && (
                      <span className="signal-search-option-badge signal-search-option-badge-network">
                        {option.network}
                      </span>
                    )}
                    {isSelected && (
                      <span className="signal-search-option-badge signal-search-option-badge-selected">
                        Added
                      </span>
                    )}
                  </Box>
                  {isSelected && <CheckCircleIcon className="signal-search-option-check" />}
                </Box>
                {option.longname && !isNetworkChild && (
                  <Typography variant="caption" color="text.secondary" className="signal-search-option-meta signal-search-option-longname">
                    {option.longname}
                  </Typography>
                )}
                {!isGroup && !isNetworkChild && option.message && (
                  <Typography variant="caption" color="text.secondary" className="signal-search-option-meta">
                    {`Message: ${option.message}`}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        noOptionsText={signalOptionsQuery.isFetching ? "Loading..." : "No signals found"}
      />
    </Box>
  );
};

export default SignalSearch;
